import {Button, Grid, Paper, Typography} from '@material-ui/core';
import {CircularProgress} from '@material-ui/core/';
import {amber, green} from '@material-ui/core/colors';
import createMuiTheme from '@material-ui/core/styles/createTheme';
import ArrowBackIosOutlinedIcon from '@material-ui/icons/ArrowBackIosOutlined';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import locale from '../locale/Localization';
import {validateDocument} from '../script/document-service';
import {ERROR_CONTENT_TOO_LARGE, UPLOAD_ACTION_TYPE} from '../utils/constants';
import ConfirmationDialog from './ConfirmationDialog';
import DocumentForm from './DocumentForm';
import DocumentValidation from './DocumentValidation';

const theme = createMuiTheme();
const styles = {
  list: {
    backgroundColor: theme.palette.background.default,
    width: 500,
    marginTop: 20
  },
  info: {
    color: theme.palette.primary.main
  },
  success: {
    color: green[600]
  },
  error: {
    color: theme.palette.error.dark
  },
  warning: {
    color: amber[700]
  },
  root: {
    width: '100%'
  },
  paper: {
    padding: theme.spacing(3, 2)
  },
  title: {
    paddingTop: '20px',
    paddingBottom: '20px',
    color: 'white'
  },
  button: {
    marginTop: '20px',
    color: 'white'
  }
};

/**
 * Component class of DocumentUpload
 */
export default class DocumentUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      hasSuccessorSignature: false,
      isSuccessor: false,
      isSuccessorSignatureConfirmed: false,
      loading: false,
      open: false,
      successorName: null,
      uploadMessage: {
        text: '',
        error: false
      },
      validationResult: null
    };
  }

  /**
   * Validate a selected File.
   *
   * @param {File} file - A file to validate.
   * @return {Boolean} - Return true if the file is a pdf, else return false.
   */
  isValidFile(file) {
    const index = file.name.lastIndexOf('.');

    if (index === -1) {
      return false;
    }

    const fileExt = file.name.substring(index + 1);
    return fileExt.toLowerCase() === 'pdf';
  }

  /**
   * Handle field changes for different fields.
   *
   * @param {Event} event - Event from change action.
   */
  handleFileChange = (event) => {
    const input = event.target;
    let messageText = '';
    let messageError = false;

    if (input.files.length === 1 && this.isValidFile(input.files[0])) {
      this.setState({file: input.files[0]});
    } else {
      this.setState({file: null});
      messageText = locale.fileChangeErrorMessage;
      messageError = true;
    }

    this.handleMessageChange(messageText, messageError, null);
    this.setState({
      hasSuccessorSignature: false,
      successorName: null
    });
  };

  /**
   * Handles the message text and color.
   *
   * @param {String} messageText - The new text to be set as message.
   * @param {Boolean} messageError - The new message error status to be set.
   * @param {Object} validationResult - The document validation informations.
   */
  handleMessageChange = (messageText, messageError, validationResult) => {
    const currentMessage = this.state.uploadMessage;
    currentMessage['text'] = messageText;
    currentMessage['error'] = messageError;
    this.setState({
      uploadMessage: currentMessage,
      validationResult: validationResult
    });
  };

  /**
   * Render message method.
   *
   * @return {String} - The function content.
   */
  renderMessage = () => {
    if (!this.state.validationResult) {
      return null;
    }

    let messageStyle = styles.success;
    const isContentTooLargeError =
      !!this.state.validationResult.error &&
      this.state.validationResult.error === ERROR_CONTENT_TOO_LARGE;

    if (this.state.uploadMessage.error) {
      messageStyle = styles.error;
    }

    return (
      <div>
        <span id='upload-message' style={messageStyle}>
          {this.state.uploadMessage.text}
          <br />
          {isContentTooLargeError && locale.errors.contentTooLarge}
        </span>
        {this.state.validationResult.info && (
          <DocumentValidation validationResult={this.state.validationResult} />
        )}
      </div>
    );
  };

  /**
   * Render HTML file input element.
   *
   * @return {string} - file input element
   */
  renderFileInputElement = () => {
    return (
      <Grid item xs={8}>
        <input
          id='file-input'
          disabled={this.state.loading}
          required
          accept='.pdf'
          type='file'
          name='file'
          onChange={(event) => {
            this.handleFileChange(event);
          }}
        />
      </Grid>
    );
  };

  handleFileUploadSubmit = () => {
    const isSuccessor =
      this.props.user.type === 'NOTARY' &&
      this.props.archiveId !== this.props.mainArchiveId;

    this.setState({loading: true, isSuccessor: isSuccessor});

    return isSuccessor ? this.handleSuccessorUpload() : this.handleFileUpload();
  };

  hasSuccessorSignatureUploadPermission = () => {
    return (
      this.props.user.archiveAccess.find(
        (access) => access.archiveId === this.props.archiveId
      )?.acceptSuccessorSignature ?? false
    );
  };

  isFileSizeExceeded = () => {
    if (!this.state.file) {
      return false;
    }

    const maxSizeInBytes = 50 * 1024 * 1024; // 50 MB in Bytes

    return this.state.file.size > maxSizeInBytes;
  };

  handleSuccessorUpload = () => {
    if (this.state.hasSuccessorSignature && this.state.successorName) {
      // Previous validation result has successor signature
      return this.setState({open: true});
    }

    validateDocument(this.state.file)
      .then((response) => {
        const notaryId = response.notaryId ?? null;
        if (!notaryId) {
          this.setState({loading: false});
          // FIXME: Implement proper error response and handling
          throw new Error(
            this.isFileSizeExceeded()
              ? locale.errors.contentTooLarge
              : locale.errors.unexpected
          );
        }

        const hasSuccessorSignature =
          !!notaryId && notaryId === this.props.user.id;
        if (!hasSuccessorSignature) {
          // Proceed with upload if the document has not been signed by the successor user
          return this.handleFileUpload();
        }

        if (!this.hasSuccessorSignatureUploadPermission()) {
          this.setState({loading: false});
          throw new Error(locale.successorSignatureUploadPermissionError);
        }

        // Require successor confirmation
        this.setState({
          open: true,
          hasSuccessorSignature: true,
          successorName: response.notarySignature.signedBy ?? ''
        });
      })
      .catch((error) => {
        this.setState({loading: false});
        this.props.raiseNotification(
          locale.documentValidationFailed + ' ' + error.message,
          'error'
        );
        return;
      });
  };

  handleSuccessorUploadSubmit = async () => {
    await this.setState({open: false, isSuccessorSignatureConfirmed: true});
    // FIXME: Avoid validating document twice
    this.handleFileUpload();
  };

  handleSuccessorUploadCancel = () => {
    this.setState({open: false, loading: false});
  };

  handleFileUpload = () => {
    this.props
      .handleFileSubmit(this.state.file, this.props.uploadActionType)
      .then((response) => {
        this.setState({loading: false});

        if (response.error) {
          this.handleMessageChange(
            locale.documentValidationFailed,
            true,
            response
          );
        } else {
          this.handleMessageChange(null, false, null);
        }
      });
  };

  renderFileUploadForm = () => {
    return (
      <Grid container spacing={3} alignItems='center'>
        <Grid item xs={9}>
          <form
            id={
              this.props.uploadActionType == UPLOAD_ACTION_TYPE.NEW_DOCUMENT
                ? 'file-upload-form'
                : 'new-version-upload-form'
            }
          >
            <Paper style={styles.paper}>
              <Grid container spacing={4}>
                {this.renderFileInputElement()}
                <Grid item xs={1}>
                  {this.state.loading && <CircularProgress size={36} />}
                </Grid>
                <Grid item xs={3}>
                  <Button
                    disabled={!this.state.file || this.state.loading}
                    variant='contained'
                    color='default'
                    onClick={this.handleFileUploadSubmit}
                  >
                    {locale.uploadButton}
                  </Button>
                </Grid>
              </Grid>
              {this.renderMessage()}
            </Paper>
          </form>
        </Grid>
        <Grid item xs></Grid>
      </Grid>
    );
  };

  /**
   * Renders document's upload form.
   *
   * @return {String} - The upload form to be rendered.
   */
  renderDocumentUploadForm = () => {
    return (
      <div id='document-upload-container' style={styles.root}>
        <DocumentForm
          archiveAccess={'WRITE'}
          documentId={this.props.documentId}
          handleDocumentSubmit={this.props.handleDocumentSubmit}
        />
      </div>
    );
  };

  canRenderDocumentUploadForm = () => {
    const allowSuccessorUpload =
      this.state.isSuccessor && !this.state.hasSuccessorSignature;
    const allowSuccessorSignatureUpload =
      this.state.isSuccessor &&
      this.state.hasSuccessorSignature &&
      this.state.isSuccessorSignatureConfirmed;

    const canRenderDocumentUploadForm =
      !this.state.isSuccessor ||
      allowSuccessorUpload ||
      allowSuccessorSignatureUpload;
    return canRenderDocumentUploadForm;
  };

  // Renders upload form depending on the action type and upload progess.
  renderUploadForm = () => {
    if (this.props.uploadActionType == UPLOAD_ACTION_TYPE.NEW_VERSION) {
      // File upload form for new document version
      return this.renderFileUploadForm();
    }

    if (this.props.uploadActionType != UPLOAD_ACTION_TYPE.NEW_DOCUMENT) {
      // Should never happen
      return null;
    }

    if (!this.props.documentId) {
      // File upload form for new document (similar to new version form)
      return this.renderFileUploadForm();
    }

    if (this.canRenderDocumentUploadForm()) {
      // Display the document upload form if not a successor
      // or document has successor signature and is confirmed.
      return this.renderDocumentUploadForm();
    }

    return null;
  };

  getConfirmationDialogText = () => {
    const userName =
      this.props.user.firstNames + ' ' + this.props.user.officialName;
    return locale.formatString(
      locale.successorSignatureArchivingDialogDescription,
      <strong>{this.props.archiveOwner}</strong>,
      <strong>{this.state.successorName ?? userName}</strong>,
      <span>&shy;</span>
    );
  };

  /**
   * React render function.
   *
   * @return {String} - The function content.
   */
  render() {
    return (
      <div>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={10}>
            <Button
              style={styles.button}
              size='small'
              onClick={() => {
                this.props.onBackLinkClick();
              }}
              startIcon={<ArrowBackIosOutlinedIcon />}
            >
              {locale.back}
            </Button>
          </Grid>
        </Grid>
        <Typography variant='h5' gutterBottom style={styles.title}>
          {this.props.uploadActionType == UPLOAD_ACTION_TYPE.NEW_DOCUMENT
            ? locale.documentUploadTitle
            : locale.newVersionUploadTitle}
        </Typography>
        {this.renderUploadForm()}
        {this.state.isSuccessor && (
          <ConfirmationDialog
            open={this.state.open}
            dialogText={this.getConfirmationDialogText()}
            title={locale.successorSignatureArchivingDialogTitle}
            handleClose={this.handleSuccessorUploadCancel}
            handleSubmit={this.handleSuccessorUploadSubmit}
          />
        )}
      </div>
    );
  }
}

DocumentUpload.propTypes = {
  raiseNotification: PropTypes.func.isRequired,
  handleFileSubmit: PropTypes.func.isRequired,
  handleDocumentSubmit: PropTypes.func,
  onBackLinkClick: PropTypes.func.isRequired,
  archiveId: PropTypes.number.isRequired,
  archiveOwner: PropTypes.string.isRequired,
  documentUploadToggle: PropTypes.func,
  mainArchiveId: PropTypes.number.isRequired,
  uploadActionType: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  documentId: PropTypes.string
};
